import React, { useState } from 'react';

/**
 * Sección unificada de Plantas + Distribución con tabs interactivos.
 * Reemplaza las dos secciones separadas (especificaciones + plantas) que
 * generaban repetición y huecos visuales en las páginas de modelo.
 *
 * Props:
 *  - modelName    : 'Fernanda' | 'Mariana'
 *  - construccion : '143 m²' (string)
 *  - lote         : '127.5 m²' (string)
 *  - recamaras    : '3 a 4'
 *  - banos        : '2.5'
 *  - cochera      : '2 autos'
 *  - levels       : [{ label, image, items: [] }, ...]
 *  - accent       : color string
 */
export default function ModeloPlantasInteractive({
  modelName,
  construccion,
  lote,
  recamaras,
  banos,
  cochera,
  levels = [],
  accent = '#f6802c',
}) {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);
  const current = levels[active] || {};

  return (
    <section id="proyecto" className="mpi-section" data-testid="mpi-section">
      <div className="page-padding">
        <div className="container-large">
          {/* Header — eyebrow + título + specs estilo home */}
          <div className="mpi-head">
            <div className="text-label" style={{ color: 'var(--brand-accent)' }}>modelo {modelName.toLowerCase()}</div>
            <h2 className="sm-h2 font-color-1">
              Distribución del <span className="serif-accent">Modelo {modelName}</span>
            </h2>
            <p className="modelos-intro">
              {construccion} de construcción sobre lote tipo de {lote}. Explora cada nivel y descubre cómo
              cada espacio fue pensado para crecer con tu familia.
            </p>
          </div>

          {/* Strip de specs clave — horizontal, compacto */}
          <div className="mpi-specs-strip" data-testid="mpi-specs-strip">
            <div className="mpi-spec">
              <span className="icon-font">crop_landscape</span>
              <div>
                <div className="mpi-spec-val">{lote}</div>
                <div className="mpi-spec-lbl">Lote tipo</div>
              </div>
            </div>
            <div className="mpi-spec">
              <span className="icon-font">square_foot</span>
              <div>
                <div className="mpi-spec-val">{construccion}</div>
                <div className="mpi-spec-lbl">Construcción</div>
              </div>
            </div>
            <div className="mpi-spec">
              <span className="icon-font">bed</span>
              <div>
                <div className="mpi-spec-val">{recamaras}</div>
                <div className="mpi-spec-lbl">Recámaras</div>
              </div>
            </div>
            <div className="mpi-spec">
              <span className="icon-font">shower</span>
              <div>
                <div className="mpi-spec-val">{banos}</div>
                <div className="mpi-spec-lbl">Baños</div>
              </div>
            </div>
            <div className="mpi-spec">
              <span className="icon-font">directions_car</span>
              <div>
                <div className="mpi-spec-val">{cochera}</div>
                <div className="mpi-spec-lbl">Cochera</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mpi-tabs" data-testid="mpi-tabs">
            {levels.map((lv, i) => (
              <button
                key={i}
                type="button"
                className={`mpi-tab${active === i ? ' active' : ''}`}
                onClick={() => setActive(i)}
                style={{ '--tab-accent': accent }}
                data-testid={`mpi-tab-${i}`}
              >
                <span className="mpi-tab-num">0{i + 1}</span>
                <span className="mpi-tab-label">{lv.label}</span>
              </button>
            ))}
          </div>

          {/* Plano + lista lado a lado */}
          <div className="mpi-grid">
            <button
              type="button"
              className="mpi-plano"
              data-testid="mpi-plano"
              onClick={() => setZoom(true)}
              aria-label="Ampliar plano"
            >
              <img loading="lazy" src={current.image} alt={`Planta ${current.label}`} />
              <span className="mpi-plano-badge">
                <span className="icon-font">zoom_in</span>
                <span>Toca para ampliar</span>
              </span>
            </button>

            <div className="mpi-list">
              <div className="mpi-list-head">
                <span className="mpi-list-num" style={{ background: accent }}>0{active + 1}</span>
                <h3 className="mpi-list-title">{current.label}</h3>
              </div>
              <ul className="mpi-items">
                {(current.items || []).map((item, i) => (
                  <li key={i}>
                    <span className="icon-font" style={{ color: accent }}>check_circle</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {zoom && (
        <div className="lightbox-modal" onClick={() => setZoom(false)} data-testid="mpi-lightbox">
          <button className="lightbox-close" onClick={() => setZoom(false)} aria-label="Cerrar">
            <span className="icon-font">close</span>
          </button>
          <button
            className="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); setActive((a) => (a - 1 + levels.length) % levels.length); }}
            aria-label="Anterior"
          >
            <span className="icon-font">chevron_left</span>
          </button>
          <img loading="lazy" src={current.image} alt={`Planta ${current.label}`} onClick={(e) => e.stopPropagation()} />
          <button
            className="lightbox-next"
            onClick={(e) => { e.stopPropagation(); setActive((a) => (a + 1) % levels.length); }}
            aria-label="Siguiente"
          >
            <span className="icon-font">chevron_right</span>
          </button>
        </div>
      )}
    </section>
  );
}
